/**
 * Sing4You Management — KARAOKE BROADCAST (v2)
 *
 * Broadcast message envelope:
 *   { type: "karaoke_audio", data: { action, ...fields }, timestamp, roomId }
 *
 * Actions:
 *   load  → { url, trackName, duration? }
 *   play  → { currentTime, duration }
 *   pause → { currentTime, duration }
 *   seek  → { currentTime, duration, isPlaying }
 *   sync  → { currentTime, isPlaying }           (periodic)
 *   stop  → {}
 *   end   → {}
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Slider,
  Tooltip,
  Typography,
} from "@mui/material";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import MicIcon from "@mui/icons-material/Mic";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import StopIcon from "@mui/icons-material/Stop";
import { ZegoExpressEngine } from "zego-express-engine-webrtc";
import { useParams } from "react-router-dom";
import type { SongItem } from "./ListofSongsManagement";
import { uploadFile } from "../../../services/api/uploadFile";

/* ─── palette ──────────────────────────────────────────────────────────── */
const K = {
  bg:      "#07070f",
  card:    "#10101c",
  border:  "rgba(168,85,247,0.22)",
  pink:    "#ff6b9d",
  cyan:    "#22d3ee",
  purple:  "#a855f7",
  yellow:  "#fbbf24",
  text:    "#ffffff",
  muted:   "rgba(255,255,255,0.5)",
  red:     "#E50914",
} as const;

const EQ_COLORS = [K.pink, K.cyan, K.purple, K.yellow];

/* ─── static EQ bar specs (pre-computed — no random in render) ──────────── */
const EQ_BARS = [
  { h: 20, dur: "0.38s", delay: "0.00s" }, { h: 32, dur: "0.45s", delay: "0.07s" },
  { h: 14, dur: "0.33s", delay: "0.14s" }, { h: 28, dur: "0.52s", delay: "0.03s" },
  { h: 22, dur: "0.41s", delay: "0.21s" }, { h: 36, dur: "0.35s", delay: "0.10s" },
  { h: 18, dur: "0.49s", delay: "0.28s" }, { h: 30, dur: "0.43s", delay: "0.05s" },
  { h: 26, dur: "0.38s", delay: "0.17s" }, { h: 12, dur: "0.56s", delay: "0.24s" },
  { h: 34, dur: "0.40s", delay: "0.09s" }, { h: 24, dur: "0.47s", delay: "0.32s" },
  { h: 16, dur: "0.36s", delay: "0.13s" }, { h: 30, dur: "0.53s", delay: "0.06s" },
  { h: 22, dur: "0.42s", delay: "0.19s" }, { h: 28, dur: "0.39s", delay: "0.26s" },
  { h: 20, dur: "0.46s", delay: "0.02s" }, { h: 36, dur: "0.34s", delay: "0.30s" },
  { h: 14, dur: "0.51s", delay: "0.16s" }, { h: 26, dur: "0.44s", delay: "0.23s" },
  { h: 32, dur: "0.37s", delay: "0.08s" }, { h: 18, dur: "0.50s", delay: "0.35s" },
  { h: 24, dur: "0.40s", delay: "0.12s" }, { h: 30, dur: "0.48s", delay: "0.20s" },
];

/* ─── floating note specs ───────────────────────────────────────────────── */
const NOTES = [
  { n: "♪", left: "7%",  dur: "4.1s", delay: "0.0s",  size: "1.4rem", col: K.pink   },
  { n: "♫", left: "18%", dur: "5.3s", delay: "0.9s",  size: "1.8rem", col: K.cyan   },
  { n: "♬", left: "31%", dur: "3.8s", delay: "1.7s",  size: "1.2rem", col: K.purple },
  { n: "♩", left: "46%", dur: "4.6s", delay: "0.4s",  size: "1.6rem", col: K.yellow },
  { n: "♪", left: "60%", dur: "5.0s", delay: "2.2s",  size: "1.3rem", col: K.pink   },
  { n: "♫", left: "74%", dur: "3.6s", delay: "1.1s",  size: "2.0rem", col: K.cyan   },
  { n: "♬", left: "86%", dur: "4.8s", delay: "2.8s",  size: "1.5rem", col: K.purple },
  { n: "♩", left: "52%", dur: "4.3s", delay: "3.4s",  size: "1.7rem", col: K.yellow },
];

/* ─── types ──────────────────────────────────────────────────────────────── */
type Phase = "idle" | "selected" | "uploading" | "ready" | "playing" | "paused" | "ended";

export interface ActiveTrackFromList {
  url: string;
  name: string;
  duration?: number;
}

export interface Sing4YouUpdatedProps {
  zg: ZegoExpressEngine | null;
  /** Track selected from Song List — when set, load it and show ready/play UI. When null, show "Select from list". */
  activeTrack?: ActiveTrackFromList | null;
  /** Full song list (same as in Song List tool) so the list is shown here too. */
  songs?: SongItem[];
  /** Called when user selects a song from the list (to load and broadcast). */
  onSongSelect?: (song: SongItem) => void;
  /** Called whenever karaoke goes live or stops so the video view can react. */
  onKaraokeChange?: (active: boolean, trackName: string) => void;
  /** Called when track metadata (e.g. duration) is loaded; use to update the song list. */
  onTrackDuration?: (url: string, duration: number) => void;
  /** Called when user clears the current track (e.g. Close) so parent can clear activeTrack. */
  onClearTrack?: () => void;
  /** Called when a track is uploaded directly here (file pick); parent can add it to the song list. */
  onTrackReady?: (name: string, url: string, duration?: number) => void;
}

/* ─── helpers ────────────────────────────────────────────────────────────── */
function isValidAudioFile(f: File) {
  const types = ["audio/mpeg","audio/wav","audio/ogg","audio/mp4","audio/aac","audio/webm","audio/flac","audio/x-m4a"];
  return types.includes(f.type) || !!f.name.toLowerCase().match(/\.(mp3|wav|ogg|m4a|aac|webm|flac)$/);
}

function fmt(s: number) {
  if (!isFinite(s) || s < 0) return "0:00";
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
}

/** First line: up to maxWords words; rest wrap below */
function formatTitleLines(title: string, maxWords = 5): React.ReactNode {
  if (!title || !String(title).trim()) return title;
  const words = String(title).trim().split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return title;
  const firstLine = words.slice(0, maxWords).join(" ");
  const rest = words.slice(maxWords).join(" ");
  return <>{firstLine}<br />{rest}</>;
}

function isActiveSong(song: SongItem, activeTrack: ActiveTrackFromList | null | undefined): boolean {
  if (!activeTrack?.url) return false;
  return song.url === activeTrack.url || (song.name === activeTrack.name && !!song.url);
}

/* ─── sub-components ─────────────────────────────────────────────────────── */

function StageLights({ active }: { active: boolean }) {
  return (
    <>
      {/* Left spotlight — pink */}
      <Box sx={{
        position: "absolute", top: 0, left: "5%",
        width: "38%", height: "160px", pointerEvents: "none", zIndex: 0,
        background: `radial-gradient(ellipse at 30% 0%, ${active ? "rgba(255,107,157,0.28)" : "rgba(255,107,157,0.10)"} 0%, transparent 70%)`,
        transition: "background 1.2s ease",
        animation: active ? "lightSweepL 5s ease-in-out infinite alternate" : "none",
        "@keyframes lightSweepL": {
          from: { opacity: 0.7 }, to: { opacity: 1 },
        },
      }} />
      {/* Centre spotlight — purple */}
      <Box sx={{
        position: "absolute", top: 0, left: "25%",
        width: "50%", height: "200px", pointerEvents: "none", zIndex: 0,
        background: `radial-gradient(ellipse at 50% 0%, ${active ? "rgba(168,85,247,0.32)" : "rgba(168,85,247,0.10)"} 0%, transparent 65%)`,
        transition: "background 1.2s ease",
        animation: active ? "lightPulseC 3s ease-in-out infinite" : "none",
        "@keyframes lightPulseC": {
          "0%,100%": { opacity: 0.8 }, "50%": { opacity: 1 },
        },
      }} />
      {/* Right spotlight — cyan */}
      <Box sx={{
        position: "absolute", top: 0, right: "5%",
        width: "38%", height: "160px", pointerEvents: "none", zIndex: 0,
        background: `radial-gradient(ellipse at 70% 0%, ${active ? "rgba(34,211,238,0.25)" : "rgba(34,211,238,0.08)"} 0%, transparent 70%)`,
        transition: "background 1.2s ease",
        animation: active ? "lightSweepR 4.5s ease-in-out infinite alternate" : "none",
        "@keyframes lightSweepR": {
          from: { opacity: 0.8 }, to: { opacity: 1 },
        },
      }} />
    </>
  );
}

function FloatingNotes() {
  return (
    <>
      {NOTES.map((n, i) => (
        <Box key={i} sx={{
          position: "absolute", left: n.left, bottom: "10%",
          fontSize: n.size, color: n.col, pointerEvents: "none",
          textShadow: `0 0 10px ${n.col}`,
          animation: `karaNoteFloat ${n.dur} ${n.delay} ease-in infinite`,
          "@keyframes karaNoteFloat": {
            "0%":   { opacity: 0,   transform: "translateY(0)     scale(0.8)" },
            "15%":  { opacity: 0.9, transform: "translateY(-30px)  scale(1)"   },
            "85%":  { opacity: 0.6, transform: "translateY(-180px) scale(1.05)" },
            "100%": { opacity: 0,   transform: "translateY(-240px) scale(0.7)" },
          },
          zIndex: 0,
        }}>
          {n.n}
        </Box>
      ))}
    </>
  );
}

function EQVisualizer({ active }: { active: boolean }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "flex-end", gap: "3px",
      height: 44, justifyContent: "center", my: 1,
    }}>
      {EQ_BARS.map((b, i) => (
        <Box key={i} sx={{
          width: 5, borderRadius: "3px 3px 0 0",
          bgcolor: EQ_COLORS[i % 4],
          boxShadow: `0 0 6px ${EQ_COLORS[i % 4]}60`,
          height: active ? `${b.h}px` : "4px",
          transformOrigin: "bottom",
          transition: active ? "none" : "height 0.4s ease",
          animation: active
            ? `eqBounce ${b.dur} ${b.delay} ease-in-out infinite alternate`
            : "none",
          "@keyframes eqBounce": {
            from: { transform: "scaleY(0.2)", opacity: 0.5 },
            to:   { transform: "scaleY(1)",   opacity: 1   },
          },
        }} />
      ))}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function Sing4YouManagementUpdated({ zg, activeTrack, songs = [], onSongSelect, onKaraokeChange, onTrackDuration, onClearTrack, onTrackReady }: Sing4YouUpdatedProps) {
  const { channel_name } = useParams<{ channel_name: string }>();

  /* ── state ── */
  const [phase, setPhase]                   = useState<Phase>("idle");
  const [file, setFile]                     = useState<File | null>(null);
  const [trackName, setTrackName]           = useState("");
  const [trackUrl, setTrackUrl]             = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError]                   = useState<string | null>(null);
  const [currentTime, setCurrentTime]       = useState(0);
  const [duration, setDuration]             = useState(0);
  const [performElapsed, setPerformElapsed] = useState(0);
  const [dragOver, setDragOver]             = useState(false);
  const [volume, setVolume]                 = useState(0.8); // MR (instrumental) volume 0–1

  /* ── refs ── */
  const audioRef        = useRef<HTMLAudioElement | null>(null);
  const syncRef         = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef     = useRef<number>(0);
  const perfTimerRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const perfStartRef    = useRef<number>(0);
  const lastAppliedUrlRef = useRef<string | null>(null);

  /* ─── performance timer ──────────────────────────────────────────────── */
  const startPerfTimer = useCallback(() => {
    if (perfTimerRef.current) return;
    perfStartRef.current = Date.now() - performElapsed * 1000;
    perfTimerRef.current = setInterval(() => {
      setPerformElapsed(Math.floor((Date.now() - perfStartRef.current) / 1000));
    }, 1000);
  }, [performElapsed]);

  const stopPerfTimer = useCallback(() => {
    if (perfTimerRef.current) { clearInterval(perfTimerRef.current); perfTimerRef.current = null; }
  }, []);

  const resetPerfTimer = useCallback(() => {
    stopPerfTimer();
    setPerformElapsed(0);
  }, [stopPerfTimer]);

  /* ─── broadcast helper ───────────────────────────────────────────────── */
  const broadcast = useCallback(async (
    action: string,
    extra: Record<string, unknown> = {}
  ) => {
    if (!zg || !channel_name) return;
    try {
      await zg.sendBroadcastMessage(
        channel_name,
        JSON.stringify({
          type: "karaoke_audio",
          data: { action, ...extra },
          timestamp: Date.now(),
          roomId: channel_name,
        })
      );
    } catch (e) {
      console.warn("[Sing4YouUpdated] broadcast failed:", e);
    }
  }, [zg, channel_name]);

  /* ─── periodic sync ──────────────────────────────────────────────────── */
  const startSync = useCallback(() => {
    if (syncRef.current) return;
    syncRef.current = setInterval(() => {
      const audio = audioRef.current;
      if (!audio || audio.paused) return;
      broadcast("sync", { currentTime: audio.currentTime, isPlaying: true });
    }, 1500);
  }, [broadcast]);

  const stopSync = useCallback(() => {
    if (syncRef.current) { clearInterval(syncRef.current); syncRef.current = null; }
  }, []);

  /* ─── sync active track from Song List ───────────────────────────────── */
  useEffect(() => {
    const rawUrl = activeTrack?.url;
    const url = typeof rawUrl === "string" ? rawUrl.trim() : "";
    if (!url) {
      lastAppliedUrlRef.current = null;
      if (phase === "idle") {
        console.log("[Sing4You] activeTrack cleared or invalid URL, staying idle", { hasTrack: !!activeTrack, urlType: typeof rawUrl });
        setTrackUrl(null);
        setTrackName("");
        setDuration(0);
        setCurrentTime(0);
        setError(activeTrack && !url ? "Track URL is missing — cannot load MP3." : null);
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = "";
          audioRef.current = null;
        }
        stopSync();
        resetPerfTimer();
      }
      return;
    }
    if (lastAppliedUrlRef.current === url) {
      return;
    }
    lastAppliedUrlRef.current = url;
    const name = activeTrack!.name;
    const dur = activeTrack!.duration ?? 0;
    console.log("[Sing4You] activeTrack set", { url: url.slice(0, 60), name });
    stopSync();
    resetPerfTimer();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    setTrackUrl(url);
    setTrackName(name);
    setDuration(dur);
    setCurrentTime(0);
    setPhase("ready");
    setError(null);
    broadcast("load", { url, trackName: name, duration: dur || undefined });
  }, [activeTrack, broadcast, stopSync, resetPerfTimer, phase]);

  /* ─── wire audio element when URL is ready ───────────────────────────── */
  useEffect(() => {
    if (!trackUrl || trackUrl.trim() === "") return;
    const urlToLoad = trackUrl.trim();
    console.log("[Sing4You] Loading MP3 track", urlToLoad.slice(0, 80));
    const audio = new Audio();
    audio.preload = "auto";
    audio.src = urlToLoad;
    audioRef.current = audio;
    audio.load();

    const onMeta  = () => {
      const d = audio.duration || 0;
      setDuration(d);
      if (d > 0) onTrackDuration?.(audio.src, d);
      console.log("[Sing4You] Track metadata loaded", { duration: d });
    };
    const onTime  = () => { setCurrentTime(audio.currentTime); progressRef.current = audio.currentTime; };
    const onEnded = () => { stopSync(); stopPerfTimer(); setPhase("ended"); broadcast("end"); onKaraokeChange?.(false, ""); };
    const onError = (e: Event) => {
      const err = (audio as HTMLAudioElement).error;
      const msg = err ? `Load failed (${err.message || err.code})` : "Audio load error";
      console.error("[Sing4You] Audio error", { url: urlToLoad, error: err, event: e });
      setError(msg);
    };
    const onCanPlay = () => {
      console.log("[Sing4You] MP3 ready to play", urlToLoad.slice(0, 60));
    };

    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("timeupdate",     onTime);
    audio.addEventListener("ended",          onEnded);
    audio.addEventListener("error",          onError);
    audio.addEventListener("canplay",       onCanPlay);
    return () => {
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("timeupdate",     onTime);
      audio.removeEventListener("ended",          onEnded);
      audio.removeEventListener("error",         onError);
      audio.removeEventListener("canplay",       onCanPlay);
      audio.pause();
      audio.src = "";
      if (audioRef.current === audio) audioRef.current = null;
    };
  }, [trackUrl, stopSync, stopPerfTimer, broadcast, onTrackDuration, onKaraokeChange]);

  /* ─── unmount cleanup ────────────────────────────────────────────────── */
  useEffect(() => () => {
    stopSync();
    stopPerfTimer();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; }
  }, [stopSync, stopPerfTimer]);

  /* ─── playback actions ───────────────────────────────────────────────── */
  const handlePlay = useCallback(async () => {
    const audio = audioRef.current;
    console.log("[Sing4You] handlePlay called", { hasAudio: !!audio, src: audio?.src?.slice(0, 50), readyState: audio?.readyState });
    if (!audio) {
      console.warn("[Sing4You] No audio element — track may not be loaded yet");
      setError("Track not ready. Select a song from the list and wait a moment.");
      return;
    }
    if (!audio.src) {
      console.warn("[Sing4You] Audio element has no src");
      setError("Track URL missing.");
      return;
    }
    try {
      await audio.play();
      console.log("[Sing4You] Play started");
      setPhase("playing");
      startSync();
      startPerfTimer();
      broadcast("play", { currentTime: audio.currentTime, duration: audio.duration, trackName });
      onKaraokeChange?.(true, trackName);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[Sing4You] Play failed", e);
      setError("Playback failed: " + msg);
    }
  }, [startSync, startPerfTimer, broadcast, onKaraokeChange, trackName]);

  const handlePause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    stopSync();
    stopPerfTimer();
    setPhase("paused");
    broadcast("pause", { currentTime: audio.currentTime, duration: audio.duration });
    onKaraokeChange?.(false, "");
  }, [stopSync, stopPerfTimer, broadcast, onKaraokeChange]);

  // Local scrub: update position while dragging — no broadcast (avoids flooding Zego)
  const handleSeekLocal = useCallback((_: Event, value: number | number[]) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const pct  = Array.isArray(value) ? value[0] : value;
    const time = (pct / 100) * duration;
    audio.currentTime = time;
    setCurrentTime(time);
  }, [duration]);

  // Commit seek: fires once when user releases — this is the only broadcast
  const handleSeekCommit = useCallback((_: Event | React.SyntheticEvent, value: number | number[]) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const pct  = Array.isArray(value) ? value[0] : value;
    const time = (pct / 100) * duration;
    audio.currentTime = time;
    setCurrentTime(time);
    broadcast("seek", { currentTime: time, duration: audio.duration, isPlaying: !audio.paused });
  }, [duration, broadcast]);

  const handleRestart = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    setCurrentTime(0);
    resetPerfTimer();
    try {
      await audio.play();
      setPhase("playing");
      startSync();
      startPerfTimer();
      broadcast("play", { currentTime: 0, duration: audio.duration, trackName });
      onKaraokeChange?.(true, trackName);
    } catch (e) {
      setError("Playback failed: " + (e as Error).message);
    }
  }, [startSync, startPerfTimer, resetPerfTimer, broadcast, onKaraokeChange, trackName]);

  const handleStop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.currentTime = 0; }
    stopSync();
    resetPerfTimer();
    setPhase("ready");
    setCurrentTime(0);
    broadcast("stop");
    onKaraokeChange?.(false, "");
  }, [stopSync, resetPerfTimer, broadcast, onKaraokeChange]);

  const handleClose = useCallback(() => {
    lastAppliedUrlRef.current = null;
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.src = ""; audioRef.current = null; }
    stopSync();
    resetPerfTimer();
    broadcast("stop");
    onKaraokeChange?.(false, "");
    setPhase("idle");
    setFile(null);
    setTrackUrl(null);
    setTrackName("");
    setCurrentTime(0);
    setDuration(0);
    setUploadProgress(0);
    setError(null);
    onClearTrack?.();
  }, [stopSync, resetPerfTimer, broadcast, onKaraokeChange, onClearTrack]);

  /* ─── direct file select (in Sing4You) ────────────────────────────────── */
  const pickFile = useCallback((f: File) => {
    if (!isValidAudioFile(f)) {
      setError("Invalid file. Use MP3, WAV, OGG, M4A, AAC, FLAC.");
      return;
    }
    setFile(f);
    setTrackName(f.name.replace(/\.[^.]+$/, ""));
    setError(null);
    setPhase("selected");
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) pickFile(f);
    e.target.value = "";
  }, [pickFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  }, [pickFile]);

  const handleUploadFromFile = useCallback(async () => {
    if (!file) return;
    setPhase("uploading");
    setUploadProgress(0);
    setError(null);
    const tick = setInterval(() => setUploadProgress((p) => (p < 85 ? p + 7 : p)), 400);
    try {
      const url = await uploadFile(file);
      clearInterval(tick);
      setUploadProgress(100);
      const urlStr = url && String(url).trim();
      if (!urlStr) throw new Error("No URL returned from upload");
      setTrackUrl(urlStr);
      setPhase("ready");
      setFile(null);
      await broadcast("load", { url: urlStr, trackName });
      onTrackReady?.(trackName, urlStr);
    } catch (e) {
      clearInterval(tick);
      setPhase("selected");
      setUploadProgress(0);
      setError(e instanceof Error ? e.message : "Upload failed");
    }
  }, [file, trackName, broadcast, onTrackReady]);

  const handleCancelDirectFile = useCallback(() => {
    setFile(null);
    setTrackName("");
    setError(null);
    setPhase("idle");
  }, []);

  /* ─── derived ────────────────────────────────────────────────────────── */
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const isPlaying   = phase === "playing";
  const isStage     = phase === "ready" || phase === "playing" || phase === "paused" || phase === "ended";

  /* ══════════════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{
      bgcolor: K.bg,
      borderRadius: 2,
      overflow: "hidden",
      color: K.text,
      border: "1px solid",
      borderColor: isPlaying ? K.purple : K.border,
      boxShadow: isPlaying
        ? `0 0 0 1px ${K.purple}40, 0 0 24px 4px ${K.purple}20`
        : "none",
      transition: "box-shadow 0.8s ease, border-color 0.8s ease",
      position: "relative",
    }}>

      {/* ══ ERROR ══ */}
      {error && (
        <Alert severity="warning" onClose={() => setError(null)}
          sx={{ m: 2, mb: 0, bgcolor: "rgba(255,152,0,0.1)", color: "#ffb74d",
            "& .MuiAlert-icon": { color: "#ffb74d" } }}>
          {error}
        </Alert>
      )}

      {/* ════════ IDLE — choose from list or pick file directly ════════ */}
      {phase === "idle" && (
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          sx={{
            position: "relative", overflow: "hidden",
            border: "2px dashed",
            borderColor: dragOver ? K.pink : "rgba(168,85,247,0.35)",
            borderRadius: 2, m: 1.5,
            p: "28px 20px",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5,
            transition: "border-color 0.2s, background 0.2s",
            bgcolor: dragOver ? "rgba(255,107,157,0.05)" : "transparent",
          }}
        >
          <Box sx={{
            width: 64, height: 64, borderRadius: "50%",
            border: `2px solid ${K.purple}60`,
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(168,85,247,0.1)",
            boxShadow: `0 0 20px ${K.purple}30`,
          }}>
            <MicIcon sx={{ fontSize: 32, color: K.purple }} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", letterSpacing: 2, color: K.text, textAlign: "center" }}>
            KARAOKE STUDIO
          </Typography>
          <Typography sx={{ color: K.muted, fontSize: "0.78rem", textAlign: "center" }}>
            Drop a track here or click to browse · or pick from the list below
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.72rem" }}>
            MP3 · WAV · FLAC · OGG · M4A
          </Typography>
          <input accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm,.flac"
            style={{ display: "none" }} id="sing4you-file-input"
            type="file" onChange={handleFileChange} />
          <label htmlFor="sing4you-file-input">
            <Box component="span" sx={{
              display: "inline-flex", alignItems: "center", gap: 0.75,
              px: 2.5, py: 1, borderRadius: 1.5, cursor: "pointer",
              background: `linear-gradient(135deg, ${K.pink}, ${K.purple})`,
              color: "#fff", fontWeight: 700, fontSize: "0.82rem",
              boxShadow: `0 4px 14px ${K.purple}40`,
              "&:hover": { opacity: 0.88 }, transition: "opacity 150ms",
            }}>
              <CloudUploadIcon sx={{ fontSize: 16 }} />
              Choose Audio File
            </Box>
          </label>
          {/* Same list as in Song List — click to load */}
          {songs.length > 0 && (
            <Box sx={{ width: "100%", mt: 1.5, maxHeight: 180, overflowY: "auto",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": { bgcolor: K.purple + "60", borderRadius: 2 },
            }}>
              <Typography sx={{ fontSize: "0.7rem", color: K.muted, mb: 0.5, px: 0.5 }}>Listed songs — click to load here</Typography>
              {songs.map((song, idx) => {
                const active = isActiveSong(song, activeTrack ?? null);
                return (
                  <Box
                    key={song.id}
                    onClick={() => onSongSelect?.(song)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1,
                      px: 1.25, py: 0.75,
                      borderRadius: 1,
                      cursor: onSongSelect ? "pointer" : "default",
                      bgcolor: active ? "rgba(168,85,247,0.15)" : "transparent",
                      "&:hover": onSongSelect ? { bgcolor: active ? "rgba(168,85,247,0.22)" : "rgba(255,255,255,0.06)" } : {},
                    }}
                  >
                    <Typography sx={{ fontSize: "0.7rem", color: K.muted, width: 18 }}>{idx + 1}</Typography>
                    <Typography sx={{ flex: 1, fontSize: "0.8rem", minWidth: 0, wordBreak: "break-word" }}>
                      ♪ {formatTitleLines(song.name)}
                    </Typography>
                    {song.duration != null && (
                      <Typography sx={{ fontSize: "0.68rem", color: K.muted, fontFamily: "monospace" }}>{fmt(song.duration)}</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ════════ SELECTED (file chosen, not yet uploaded) ════════ */}
      {phase === "selected" && file && (
        <Box sx={{ p: 2 }}>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            p: 1.5, mb: 2,
            border: "1px solid", borderColor: K.border,
            borderRadius: 1.5, bgcolor: "rgba(168,85,247,0.07)",
          }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 1,
              bgcolor: "rgba(168,85,247,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <AudioFileIcon sx={{ color: K.purple }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {file.name}
              </Typography>
              <Typography sx={{ color: K.muted, fontSize: "0.72rem" }}>
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </Typography>
            </Box>
            <Tooltip title="Remove">
              <IconButton size="small" onClick={handleCancelDirectFile}
                sx={{ color: K.muted, "&:hover": { color: K.pink } }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box component="button" onClick={handleUploadFromFile} sx={{
              flex: 1, py: 1.25, borderRadius: 1.5, border: "none", cursor: "pointer",
              background: `linear-gradient(135deg, ${K.pink}, ${K.purple})`,
              color: "#fff", fontWeight: 700, fontSize: "0.85rem",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75,
              boxShadow: `0 4px 16px ${K.purple}40`,
              "&:hover": { opacity: 0.88 }, transition: "opacity 150ms",
            }}>
              <CloudUploadIcon sx={{ fontSize: 18 }} />
              Upload &amp; Prepare Stage
            </Box>
            <Box component="button" onClick={handleCancelDirectFile} sx={{
              px: 1.5, py: 1.25, borderRadius: 1.5,
              border: "1px solid", borderColor: K.border,
              cursor: "pointer", bgcolor: "transparent", color: K.muted,
              "&:hover": { color: K.text, borderColor: K.muted },
            }}>
              Cancel
            </Box>
          </Box>
        </Box>
      )}

      {/* ════════ UPLOADING ════════ */}
      {phase === "uploading" && (
        <Box sx={{ px: 2.5, py: 3, textAlign: "center", position: "relative", overflow: "hidden" }}>
          <CircularProgress size={44} thickness={3}
            sx={{ mb: 2, color: K.purple, filter: `drop-shadow(0 0 8px ${K.purple}80)` }} />
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", letterSpacing: 1, mb: 0.5 }}>
            PREPARING STAGE
          </Typography>
          <Typography sx={{ color: K.muted, fontSize: "0.78rem", mb: 2 }}>
            Uploading {file?.name}…
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} sx={{
            borderRadius: 2, height: 5,
            bgcolor: "rgba(255,255,255,0.08)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 2,
              background: `linear-gradient(90deg, ${K.pink}, ${K.purple}, ${K.cyan})`,
              boxShadow: `0 0 10px ${K.purple}80`,
            },
          }} />
          <Typography sx={{ color: K.muted, fontSize: "0.72rem", mt: 0.75 }}>
            {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* ════════ STAGE (ready / playing / paused / ended) ════════ */}
      {isStage && (
        <Box sx={{ position: "relative", overflow: "hidden" }}>

          {/* Stage lighting cones */}
          <StageLights active={isPlaying} />

          {/* Floating notes when live */}
          {isPlaying && <FloatingNotes />}

          <Box sx={{ position: "relative", zIndex: 1, p: 2 }}>

            {/* ── Top row: status badge + performance timer ── */}
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2,
            }}>
              {/* Status pill */}
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                px: 1.25, py: 0.4,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: isPlaying ? `${K.pink}80` : phase === "paused" ? `${K.yellow}60` :
                             phase === "ended" ? `${K.cyan}60` : `${K.purple}50`,
                bgcolor: isPlaying ? "rgba(255,107,157,0.1)" : phase === "paused" ? "rgba(251,191,36,0.08)" :
                         phase === "ended" ? "rgba(34,211,238,0.08)" : "rgba(168,85,247,0.08)",
              }}>
                {isPlaying && (
                  <Box sx={{
                    width: 7, height: 7, borderRadius: "50%", bgcolor: K.pink,
                    animation: "liveDotBeat 1.2s ease-in-out infinite",
                    "@keyframes liveDotBeat": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.2 } },
                    boxShadow: `0 0 6px ${K.pink}`,
                  }} />
                )}
                <Typography sx={{
                  fontSize: "0.68rem", fontWeight: 800, letterSpacing: 1,
                  color: isPlaying ? K.pink : phase === "paused" ? K.yellow :
                         phase === "ended" ? K.cyan : K.purple,
                }}>
                  {isPlaying ? "● BROADCASTING" : phase === "paused" ? "⏸ PAUSED" :
                   phase === "ended" ? "✓ ENDED" : "READY"}
                </Typography>
              </Box>

              {/* Timer */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {(isPlaying || phase === "paused") && (
                  <Typography sx={{ fontSize: "0.72rem", color: K.muted, fontFamily: "monospace" }}>
                    On stage {fmt(performElapsed)}
                  </Typography>
                )}
                {/* Close / change track */}
                <Tooltip title="End session / change track">
                  <IconButton size="small" onClick={handleClose}
                    sx={{ color: K.muted, "&:hover": { color: K.pink }, ml: 0.5 }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* ── Track name (neon) ── */}
            <Box sx={{ textAlign: "center", mb: 1.5, px: 1 }}>
              <Typography sx={{
                fontWeight: 900,
                fontSize: { xs: "1rem", sm: "1.15rem" },
                letterSpacing: 2,
                textTransform: "uppercase",
                minWidth: 0, wordBreak: "break-word",
                color: K.text,
                textShadow: isPlaying
                  ? `0 0 16px ${K.pink}, 0 0 32px ${K.purple}80`
                  : `0 0 8px ${K.purple}60`,
                transition: "text-shadow 0.8s ease",
                animation: isPlaying ? "titleGlow 2.5s ease-in-out infinite" : "none",
                "@keyframes titleGlow": {
                  "0%,100%": { textShadow: `0 0 14px ${K.pink}, 0 0 28px ${K.purple}60` },
                  "50%":     { textShadow: `0 0 22px ${K.cyan}, 0 0 44px ${K.purple}80` },
                },
              }}>
                ♪ {formatTitleLines(trackName)}
              </Typography>
            </Box>

            {/* ── EQ visualizer ── */}
            <EQVisualizer active={isPlaying} />

            {/* ── Ended banner ── */}
            {phase === "ended" && (
              <Box sx={{
                textAlign: "center", py: 1, mb: 1,
                border: "1px solid", borderColor: `${K.cyan}40`,
                borderRadius: 1.5, bgcolor: "rgba(34,211,238,0.06)",
              }}>
                <Typography sx={{ color: K.cyan, fontSize: "0.8rem", fontWeight: 600 }}>
                  🎉 Performance complete — restart or load a new track
                </Typography>
              </Box>
            )}

            {/* ── Progress / seek slider ── */}
            <Box sx={{ px: 0.5, mb: 0.25 }}>
              <Slider
                size="small"
                value={progressPct} min={0} max={100} step={0.1}
                onChange={handleSeekLocal}
                onChangeCommitted={handleSeekCommit}
                disabled={phase === "ready" || phase === "ended"}
                sx={{
                  p: 0,
                  "& .MuiSlider-rail": {
                    bgcolor: "rgba(255,255,255,0.12)", height: 4,
                  },
                  "& .MuiSlider-track": {
                    border: "none", height: 4,
                    background: `linear-gradient(90deg, ${K.pink}, ${K.purple}, ${K.cyan})`,
                    boxShadow: isPlaying ? `0 0 8px ${K.purple}80` : "none",
                  },
                  "& .MuiSlider-thumb": {
                    width: 14, height: 14,
                    bgcolor: K.text,
                    boxShadow: `0 0 0 3px ${K.purple}80`,
                    "&:hover, &.Mui-focusVisible": {
                      boxShadow: `0 0 0 5px ${K.pink}60`,
                    },
                  },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              />
            </Box>

            {/* Time row */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              <Typography sx={{ color: K.muted, fontSize: "0.72rem", fontFamily: "monospace" }}>
                {fmt(currentTime)}
              </Typography>
              <Typography sx={{ color: K.muted, fontSize: "0.72rem", fontFamily: "monospace" }}>
                {duration > 0 ? fmt(duration) : "--:--"}
              </Typography>
            </Box>

            {/* ── Controls ── */}
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mb: 2,
            }}>
              {/* Restart */}
              <Tooltip title="Restart from beginning">
                <span>
                  <IconButton onClick={handleRestart} disabled={phase === "ready"}
                    sx={{
                      color: K.muted,
                      "&:hover": { color: K.cyan, bgcolor: "rgba(34,211,238,0.1)" },
                      "&.Mui-disabled": { opacity: 0.25 },
                    }}>
                    <ReplayIcon />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Play / Pause — big */}
              {phase !== "playing" ? (
                <Tooltip title={phase === "ended" ? "Restart" : "Go Live"}>
                  <IconButton
                    onClick={phase === "ended" ? handleRestart : handlePlay}
                    sx={{
                      width: 58, height: 58,
                      background: `linear-gradient(135deg, ${K.pink}, ${K.purple})`,
                      color: "#fff",
                      boxShadow: `0 4px 20px ${K.purple}60`,
                      "&:hover": { transform: "scale(1.06)", boxShadow: `0 6px 28px ${K.pink}70` },
                      transition: "transform 0.15s, box-shadow 0.15s",
                    }}>
                    <PlayArrowIcon sx={{ fontSize: 30 }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Pause broadcast">
                  <IconButton onClick={handlePause} sx={{
                    width: 58, height: 58,
                    background: `linear-gradient(135deg, ${K.pink}, ${K.purple})`,
                    color: "#fff",
                    boxShadow: `0 4px 20px ${K.purple}60`,
                    animation: "playRing 2s ease-in-out infinite",
                    "@keyframes playRing": {
                      "0%,100%": { boxShadow: `0 0 0 0 ${K.pink}60, 0 4px 20px ${K.purple}60` },
                      "50%":     { boxShadow: `0 0 0 8px ${K.pink}00, 0 4px 20px ${K.purple}60` },
                    },
                    "&:hover": { transform: "scale(1.06)" },
                    transition: "transform 0.15s",
                  }}>
                    <PauseIcon sx={{ fontSize: 30 }} />
                  </IconButton>
                </Tooltip>
              )}

              {/* Stop */}
              <Tooltip title="Stop & rewind">
                <span>
                  <IconButton onClick={handleStop} disabled={phase === "ready"}
                    sx={{
                      color: K.muted,
                      "&:hover": { color: K.pink, bgcolor: "rgba(255,107,157,0.1)" },
                      "&.Mui-disabled": { opacity: 0.25 },
                    }}>
                    <StopIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {/* ── Status hint ── */}
            <Box sx={{
              textAlign: "center",
              py: 0.75, px: 1.5,
              borderRadius: 1.5,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}>
              <Typography sx={{ color: K.muted, fontSize: "0.73rem" }}>
                {phase === "ready"   && "🎤 Stage ready — press ▶ to go live to all participants"}
                {phase === "playing" && "🌐 Broadcasting live · audience hears your karaoke track in sync"}
                {phase === "paused"  && "⏸ Paused · audience track is also paused"}
                {phase === "ended"   && "🎉 Track finished · press ▶ to replay or × to load a new track"}
              </Typography>
            </Box>

            {/* ── Song list (same as in Song List tool) — click to switch track ── */}
            {songs.length > 0 && (
              <Box sx={{ mt: 1.5, maxHeight: 140, overflowY: "auto",
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": { bgcolor: K.purple + "60", borderRadius: 2 },
                border: "1px solid", borderColor: "rgba(255,255,255,0.06)", borderRadius: 1,
              }}>
                <Typography sx={{ fontSize: "0.68rem", color: K.muted, px: 1, py: 0.5 }}>Listed songs</Typography>
                {songs.map((song, idx) => {
                  const active = isActiveSong(song, activeTrack ?? null);
                  return (
                    <Box
                      key={song.id}
                      onClick={() => onSongSelect?.(song)}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1,
                        px: 1.25, py: 0.5,
                        cursor: onSongSelect ? "pointer" : "default",
                        bgcolor: active ? "rgba(168,85,247,0.15)" : "transparent",
                        "&:hover": onSongSelect ? { bgcolor: active ? "rgba(168,85,247,0.22)" : "rgba(255,255,255,0.06)" } : {},
                      }}
                    >
                      <Typography sx={{ fontSize: "0.68rem", color: K.muted, width: 16 }}>{idx + 1}</Typography>
                      <Typography sx={{ flex: 1, fontSize: "0.75rem", minWidth: 0, wordBreak: "break-word" }}>
                        ♪ {formatTitleLines(song.name)}
                      </Typography>
                      {song.duration != null && (
                        <Typography sx={{ fontSize: "0.65rem", color: K.muted, fontFamily: "monospace" }}>{fmt(song.duration)}</Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

          </Box>{/* /zIndex:1 content */}
        </Box>
      )}

    </Box>
  );
}
